import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, Clock, Video, Building2, Phone, MapPin, User, ArrowRight } from "lucide-react";
import { format, isToday, isTomorrow, addDays, startOfDay } from "date-fns";

interface UpcomingSessionsWidgetProps {
  clinicianId: string;
  onViewSession?: (sessionId: string) => void;
}

export function UpcomingSessionsWidget({ clinicianId, onViewSession }: UpcomingSessionsWidgetProps) {
  const { data: sessions, isLoading } = useQuery({
    queryKey: ['upcoming-sessions-widget', clinicianId],
    queryFn: async () => {
      const today = startOfDay(new Date());
      const nextWeek = addDays(today, 7);

      const { data, error } = await supabase
        .from('therapy_sessions')
        .select('*')
        .eq('clinician_id', clinicianId)
        .gte('session_date', today.toISOString().split('T')[0])
        .lte('session_date', nextWeek.toISOString().split('T')[0])
        .neq('status', 'cancelled')
        .order('session_date', { ascending: true })
        .order('session_time', { ascending: true });

      if (error) throw error;

      // Get client names
      const clientIds = [...new Set(data?.map(s => s.client_id) || [])];
      const { data: profiles } = await supabase
        .from('profiles')
        .select('id, display_name')
        .in('id', clientIds);

      const profileMap = new Map(profiles?.map(p => [p.id, p.display_name]) || []);

      return data?.map(session => ({
        ...session,
        client_name: profileMap.get(session.client_id) || 'Client'
      })) || [];
    },
    staleTime: 2 * 60 * 1000 // 2 minutes
  });

  const getLocationIcon = (locationType: string) => {
    switch (locationType) {
      case 'video': return <Video className="h-4 w-4 text-blue-500" />;
      case 'in_person': return <Building2 className="h-4 w-4 text-green-500" />;
      case 'phone': return <Phone className="h-4 w-4 text-amber-500" />;
      default: return <MapPin className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getDayLabel = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) return 'Today';
    if (isTomorrow(date)) return 'Tomorrow';
    return format(date, 'EEEE');
  };

  const getDayBadge = (dateStr: string) => {
    const date = new Date(dateStr);
    if (isToday(date)) {
      return <Badge className="bg-primary/10 text-primary border-primary/20">Today</Badge>;
    }
    if (isTomorrow(date)) {
      return <Badge className="bg-accent/10 text-accent border-accent/20">Tomorrow</Badge>;
    }
    return null;
  };

  // Group sessions by day
  const sessionsByDay = sessions?.reduce((acc, session) => {
    const day = session.session_date;
    if (!acc[day]) acc[day] = [];
    acc[day].push(session);
    return acc;
  }, {} as Record<string, typeof sessions>) || {};

  if (isLoading) {
    return (
      <Card className="bg-card/50 border-border/30">
        <CardContent className="py-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
        </CardContent>
      </Card>
    );
  }

  const totalSessions = sessions?.length || 0;
  const todaySessions = sessions?.filter(s => isToday(new Date(s.session_date))).length || 0;

  return (
    <Card className="bg-card/50 border-border/30">
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="h-5 w-5 text-primary" />
            Upcoming Sessions
          </div>
          <div className="flex items-center gap-2">
            {todaySessions > 0 && (
              <Badge className="bg-primary text-primary-foreground">{todaySessions} today</Badge>
            )}
            <Badge variant="outline">{totalSessions} this week</Badge>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {totalSessions === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Calendar className="h-10 w-10 mx-auto mb-2 opacity-30" />
            <p>No sessions scheduled</p>
            <p className="text-sm">Your week is clear!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(sessionsByDay).slice(0, 4).map(([day, daySessions]) => (
              <div key={day}>
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-sm font-medium text-foreground">{getDayLabel(day)}</span>
                  <span className="text-xs text-muted-foreground">
                    {format(new Date(day), 'MMM d')}
                  </span>
                  {getDayBadge(day)}
                </div>
                <div className="space-y-2 ml-2">
                  {(daySessions || []).map((session: any) => (
                    <div 
                      key={session.id}
                      className="flex items-center gap-3 p-2 rounded-lg bg-muted/30 border border-border/20 hover:bg-muted/50 transition-colors cursor-pointer"
                      onClick={() => onViewSession?.(session.id)}
                    >
                      <div className="text-center min-w-[50px]">
                        <p className="text-sm font-bold text-foreground">
                          {session.session_time?.slice(0, 5)}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {session.duration_minutes}m
                        </p>
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3 text-muted-foreground" />
                          <p className="font-medium text-foreground text-sm truncate">
                            {session.client_name}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          {getLocationIcon(session.location_type)}
                          <span className="capitalize">{session.location_type.replace('_', ' ')}</span>
                          <span>•</span>
                          <span>{session.session_type}</span>
                        </div>
                      </div>
                      <ArrowRight className="h-4 w-4 text-muted-foreground" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
